import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  useTheme
} from "@carrotfertility/carotene-core"
import { useWatch } from "@carrotfertility/carotene-core-x"
import React, { useMemo } from "react"
import { FieldName } from "./CarrotPlanFormTypes"
import { Journeys } from "../../types/Journeys"
import { MemberJourneyData } from "../../types/MemberJourney"
import { NONE_OPTION, SOMETHING_ELSE_ENUM } from "./JourneySelection"

type JourneyChangeConfirmationProps = {
  memberJourney: MemberJourneyData | undefined
  benefitEnabledJourneyData: Journeys | undefined
  onConfirm: () => void
  onCancel: () => void
}

export default function JourneyChangeConfirmation({
  memberJourney,
  benefitEnabledJourneyData,
  onConfirm,
  onCancel
}: JourneyChangeConfirmationProps): JSX.Element {
  const theme = useTheme()
  const primaryJourney = useWatch({ name: FieldName.primaryJourney })
  const primaryJourneyPhase = useWatch({ name: FieldName.primaryJourneyPhase })

  const displayNames = useMemo(
    () => getJourneyDisplayNames(memberJourney, benefitEnabledJourneyData, primaryJourney, primaryJourneyPhase),
    [memberJourney, benefitEnabledJourneyData, primaryJourney, primaryJourneyPhase]
  )

  return (
    <Dialog aria-labelledby="journey-change-confirmation-title" open={true}>
      <DialogContent>
        <DialogContentText>
          Changing the member’s journey and/or phase will update the information displayed to the member in their
          portal.
        </DialogContentText>
        <DialogContentText>
          Please confirm the change from {displayNames.currentMemberJourneyName} /
          {displayNames.currentMemberJourneyPhaseName} to {displayNames.selectedPrimaryJourneyName} /
          {displayNames.selectedPrimaryJourneyPhaseName} before continuing.
        </DialogContentText>
        <DialogActions sx={{ paddingTop: theme.spacing(theme.tokens.spacing.sm) }}>
          <Button color="secondary" variant="outlined" onClick={onCancel}>
            Cancel
          </Button>
          <Button color="primary" variant="contained" onClick={onConfirm}>
            Confirm change
          </Button>
        </DialogActions>
      </DialogContent>
    </Dialog>
  )
}

export type JourneyDisplayNames = {
  currentMemberJourneyName: string
  currentMemberJourneyPhaseName: string
  selectedPrimaryJourneyName: string
  selectedPrimaryJourneyPhaseName: string
}

/**
 * Resolves journey and journey stage display names for the current and selected journey values.
 * The current journey comes from the member's existing data, while the selected journey comes from the form.
 */
export function getJourneyDisplayNames(
  memberJourney: MemberJourneyData | null | undefined,
  benefitEnabledJourneyData: Journeys | undefined,
  primaryJourneyId: number,
  primaryJourneyPhaseId: number
): JourneyDisplayNames {
  const NONE_LABEL = NONE_OPTION.label

  if (!benefitEnabledJourneyData) {
    return {
      currentMemberJourneyName: NONE_LABEL,
      currentMemberJourneyPhaseName: NONE_LABEL,
      selectedPrimaryJourneyName: NONE_LABEL,
      selectedPrimaryJourneyPhaseName: NONE_LABEL
    }
  }

  // Get current journey from memberJourney data
  const currentJourneyEnum = memberJourney?.currentMemberJourney.withInProgressRouting.journey
  const currentJourneyStageEnum = memberJourney?.currentMemberJourney.withInProgressRouting.journeyStage
  const currentJourney = benefitEnabledJourneyData.find((journey) => journey.journeyEnumName === currentJourneyEnum)
  const currentJourneyStage = currentJourney?.stages.find(
    (stage) => stage.journeyStageEnumName === currentJourneyStageEnum
  )

  // Get selected primary journey from form values
  const selectedJourney = benefitEnabledJourneyData.find((journey) => journey.journeyId === primaryJourneyId)
  const selectedJourneyStage = selectedJourney?.stages.find((stage) => stage.journeyStageId === primaryJourneyPhaseId)

  return {
    currentMemberJourneyName: currentJourney?.journeyDisplayName || NONE_LABEL,
    currentMemberJourneyPhaseName: currentJourneyStage?.journeyStageShortName || NONE_LABEL,
    selectedPrimaryJourneyName: selectedJourney?.journeyDisplayName || NONE_LABEL,
    selectedPrimaryJourneyPhaseName: selectedJourneyStage?.journeyStageShortName || NONE_LABEL
  }
}

export function hasJourneyChanged(
  memberJourney: MemberJourneyData | undefined,
  benefitEnabledJourneyData: Journeys | undefined,
  primaryJourneyId: number,
  primaryJourneyPhaseId: number
): boolean {
  if (!memberJourney || !benefitEnabledJourneyData) {
    return false
  }

  const currentMemberJourneyEnum = memberJourney.currentMemberJourney.withInProgressRouting.journey
  const currentMemberJourneyPhaseEnum = memberJourney.currentMemberJourney.withInProgressRouting.journeyStage

  if (currentMemberJourneyEnum === SOMETHING_ELSE_ENUM) {
    return false
  }

  const selectedPrimaryJourneyEnum = benefitEnabledJourneyData.find((journey) => journey.journeyId === primaryJourneyId)
    ?.journeyEnumName
  const selectedPrimaryJourneyPhaseEnum = benefitEnabledJourneyData
    .find((journey) => journey.journeyId === primaryJourneyId)
    ?.stages.find((stage) => stage.journeyStageId === primaryJourneyPhaseId)?.journeyStageEnumName

  return (
    currentMemberJourneyEnum !== selectedPrimaryJourneyEnum ||
    currentMemberJourneyPhaseEnum !== selectedPrimaryJourneyPhaseEnum
  )
}
